<template>
  <div>
    <v-toolbar elevation="1" density="compact" class="file-toolbar-container">
      <v-app-bar-nav-icon disabled variant="flat" class="logo">
        <v-img src="./logos/itemis_CREATE_Bildmarke_Flat_RGB.svg" style="position: inherit; object-fit: cover"></v-img>
      </v-app-bar-nav-icon>
      <v-tooltip location="bottom" :text="dirtyStateTooltip">
        <template #activator="{ props }">
          <v-text-field
            v-bind="props"
            v-model="statechartNameTemp"
            data-testid="fileToolbar.title-input"
            class="guide-statechart-name"
            hide-details
            variant="solo"
            density="compact"
            :bg-color="statechartNameBgColor"
            @update:focused="
              (focused) => {
                if (!focused) {
                  statechartName = validateStatechartName(statechartNameTemp);
                }
              }
            "
            ><template #prepend-inner>
              <v-icon icon="mdi-cloud-outline" />
            </template>
            <template #append-inner>
              <v-icon :color="saveStateColor" :icon="isDirty ? 'mdi-alert-outline' : 'mdi-check-circle-outline'" />
            </template> </v-text-field></template
      ></v-tooltip>
      <open-item />
      <download-item :statechart-name="statechartName" />
      <delete-item />
      <generate-code-item :statechart-name="statechartName" :class="{ unclickable: !hasGenerateFeature }" />
      <help-item />
      <publish-player-item :statechart-name="statechartName" />
    </v-toolbar>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, inject, onMounted, onBeforeUnmount } from "vue";
import { Statechart } from "../mvc/statechart";
import { ModelEventBus, ModelLoaded } from "../../event-bus";
import OpenItem from "./OpenItem/OpenItem.vue";
import DownloadItem from "./DownloadItem/DownloadItem.vue";
import DeleteItem from "./DeleteItem/DeleteItem.vue";
import HelpItem from "./HelpItem/HelpItem.vue";
import GenerateCodeItem from "./GenerateCodeItem/GenerateCodeItem.vue";
import RappidContext from "../rappid/rappid-context";
import { DirtyState } from "../services/dirty-state";
import PublishPlayerItem from "./PublishPlayerItem/PublishPlayerItem.vue";
import { PreferenceStore } from "../services/preference-store";
import { untitledStatechart, validateStatechartName } from "../utils/validateStatechartName";
import { Subscription } from "rxjs/internal/Subscription";
import { useUserStore } from "../../../components/Authentication/store/userStore";
import { CREATEFeature } from "../../../components/TermsAndConditions/CREATEFeature";

const statechart = ref<Statechart | null>(null);
const statechartName = ref<string>("");
const statechartNameTemp = ref<string>("");
const isDirty = ref<boolean>(false);
const themeName = ref<string>(PreferenceStore.INSTANCE.theme.name);

const userStore = useUserStore();
const hasGenerateFeature = computed(
  () => userStore.features.includes(CREATEFeature.GENERATE_CODE) && userStore.remainingDays > 0
);

const rappidContext: RappidContext = inject<RappidContext>("rappidContext");
const dirtyState: DirtyState = inject<DirtyState>("dirtyState");

const modelNameListener = (evt) => {
  const newName = statechart.value?.getName() || "No Title";
  if (newName === statechartName.value) return;
  statechartName.value = newName;
  statechartNameTemp.value = newName;
  document.title = newName;
};

function setStatechart(event: ModelLoaded) {
  if (statechart.value) {
    statechart.value.off("change:attrs", modelNameListener);
  }
  statechart.value = event.statechart;
  statechartName.value = statechart.value?.getName() || "No Title";
  statechart.value.on("change:attrs", modelNameListener);
}

const setDirtyState = (value: boolean) => (isDirty.value = value);

const dirtyStateSub = ref<Subscription>();

onMounted(() => {
  ModelEventBus.on("modelLoaded", setStatechart);
  dirtyStateSub.value = dirtyState.observable.subscribe(setDirtyState);

  PreferenceStore.INSTANCE.on("change:theme", (_, value) => {
    themeName.value = value?.name;
  });
});

onBeforeUnmount(() => {
  ModelEventBus.off("modelLoaded", setStatechart);
  dirtyStateSub.value.unsubscribe();
});

watch(statechartName, function (value: string) {
  if (value === "") {
    value = untitledStatechart;
  }
  statechartNameTemp.value = value;
  rappidContext.commandManager.initBatchCommand();
  statechart.value?.setName(value);
  rappidContext.commandManager.storeBatchCommand();
  // sets browser tab title
  document.title = value;
});

const statechartNameBgColor = computed(() => {
  if (themeName.value === "dark") {
    return "contrast-grey-medium";
  }
  return "white";
});

const dirtyStateTooltip = computed(() => {
  return isDirty.value ? "Unsaved changes" : "Statechart saved to cloud";
});

const saveStateColor = computed(() => {
  return isDirty.value ? "warning" : "success";
});
</script>
<style scoped>
.v-theme--light.v-btn--disabled.v-btn--variant-flat,
.v-theme--dark.v-btn--disabled.v-btn--variant-flat {
  color: rgba(var(--v-theme-contrast-grey-low));
}

.logo {
  --v-theme-surface: rgb(var(--v-theme-contrast-grey-low));
}
</style>

<style>
.file-toolbar-container {
  flex-grow: 0;
  z-index: 1;
}
.unclickable {
  pointer-events: none;
  opacity: 0.6;
}
.file-toolbar-container .v-text-field input {
  min-width: 180px;
}

/* Media queries */
@media (max-width: 959.98px) {
  .file-toolbar-container .v-text-field input {
    min-width: 130px;
  }
}
@media (max-width: 599.98px) {
  .file-toolbar-container .v-text-field input {
    min-width: 110px;
  }
}
</style>
